// App.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { UserProvider, useUser } from './contexts/UserContext';
import { MealPlanProvider, useMealPlan } from './contexts/MealPlanContext';
import UserWizard from './components/UserWizard/UserWizard';
import MealPlanComponent from './components/MealPlan/MealPlan';
import MyMealPlans from './components/Member/MyMealPlans';
import GroceriesPage from './components/Member/GroceriesPage';
import RecipeDetailPage from './components/Member/RecipeDetailPage';
import CookingModePage from './components/Member/CookingModePage';
import SharedGroceriesPage from './components/Member/SharedGroceriesPage';
import { Header, ScrollToTop } from './components/Common';
import { initializeUserCountry, getUserCountry } from './services/apiService';
import LandingPage from './components/Landing/LandingPage';
import AboutUs from './components/Pages/AboutUs';
import PrivacyPolicy from './components/Pages/PrivacyPolicy';
import CookiePolicy from './components/Pages/CookiePolicy';
import ContactUs from './components/Pages/ContactUs';
import Premium from './components/Pages/Premium';
import PremiumSuccess from './components/Pages/PremiumSuccess';
import Testing from './components/Pages/Testing';
import MyProfile from './components/Member/MyProfile';
import FavoriteMeals from './components/Member/FavoriteMeals';
import PromoLandingPage from './components/Promo/PromoLandingPage';
import NotFound from './components/Pages/NotFound';
import CookieConsent from './components/CookieConsent/CookieConsent';
import AvisoLegal from './components/Pages/AvisoLegal';
import './App.css';
import TriesLeftDrawer from './components/PremiumPromo/TriesLeftDrawer';
import ReplaceMealPlanDrawer from './components/MealPlan/ReplaceMealPlanDrawer';
import { getFingerprint } from './utils/fingerprint';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { 
  ReviewMealsGuard, 
  MyMealsGuard, 
  WizardGuard, 
  LandingGuard 
} from './components/RouteGuards';
import useNavigationBlocker from './hooks/useNavigationBlocker';
import { clearInProgressMealPlan, hasInProgressMealPlan, getInProgressMealPlan } from './utils/inProgressMealPlan';

// Admin Panel
import AdminApp from './admin/AdminApp';

// Analytics
import { initAmplitude, setUserProperties } from './analytics';
import { isAnalyticsAllowed, initConsentedAnalytics } from './utils/cookieConsent';

// PWA
import { registerServiceWorker } from './services/pushNotificationService';
import InstallPrompt from './components/PWA/InstallPrompt';
import NotificationPrompt from './components/PWA/NotificationPrompt';

// Initialize i18n (must be imported before any component that uses translations)
import './services/i18n';
import { getCurrentLanguage } from './services/i18n';
import backendClient from './services/backendClient';

// Capacitor (Android native app)
import { isNativeApp, addDeepLinkListener, addBackButtonListener, exitApp, initStatusBar, hideSplashScreen, closeExternalBrowser } from './utils/capacitorBridge';
import { isBodyScrollLocked } from './utils/bodyScrollLock';

// Safari viewport height fix
function setSafeViewportHeight() {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
}

const APP_RECOVERY_RELOAD_KEY = '9meals_last_app_recovery_reload';

const reloadAppOnce = (reason) => {
  try {
    const lastReload = Number(sessionStorage.getItem(APP_RECOVERY_RELOAD_KEY) || 0);
    if (Date.now() - lastReload < 15000) return;
    sessionStorage.setItem(APP_RECOVERY_RELOAD_KEY, String(Date.now()));
  } catch (error) {
    // sessionStorage can be unavailable in private contexts; reload is still the safest recovery.
  }
  console.warn(`[App] Reloading to recover from ${reason}`);
  window.location.reload();
};

const getErrorMessage = (event) => {
  const reason = event?.reason || event?.error || event;
  return String(
    reason?.message ||
    reason?.stack ||
    event?.message ||
    reason ||
    ''
  );
};

const isRecoverableAssetError = (message) => (
  /ChunkLoadError/i.test(message) ||
  /Loading chunk \d+ failed/i.test(message) ||
  /Failed to fetch dynamically imported module/i.test(message) ||
  /Importing a module script failed/i.test(message)
);

// Create a wrapper component to use context
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [wizardCompleted, setWizardCompleted] = useState(false);
  const [userCountry, setUserCountry] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasSavedMealPlan, setHasSavedMealPlan] = useState(false);
  const [showTriesLeftDrawer, setShowTriesLeftDrawer] = useState(false);
  const [triesLeft, setTriesLeft] = useState(3);
  const [isPremium, setIsPremium] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showLeaveMealPlanDrawer, setShowLeaveMealPlanDrawer] = useState(false);
  const [savedCookingStyle, setSavedCookingStyle] = useState(null); // Cooking style from saved meal plan
  const [resumeTick, setResumeTick] = useState(0);
  
  // Get mealPlan context with proper destructuring
  const { 
    mealPlan, 
    productList, 
    totalCost, 
    maxStorageTime, 
    leftovers,
    shoppingModeState,
    haveItState,
    setMealPlan,
    setProductList,
    setTotalCost,
    setMaxStorageTime,
    setLeftovers,
    setShoppingModeState,
    setHaveItState,
    setMyProducts,
    resetMealPlan
  } = useMealPlan() || {};

  // User context available for future use
  useUser();

  // Determine if we're on a route that needs navigation blocking
  const isOnReviewMeals = location.pathname === '/reviewmeals';
  const isOnMyMeals = location.pathname === '/mymeals';
  const shouldBlockNavigation = isOnReviewMeals || isOnMyMeals;

  // Use ref to always have the latest isPremium value (avoids stale closure in popstate handler)
  const isPremiumRef = useRef(isPremium);
  useEffect(() => { isPremiumRef.current = isPremium; }, [isPremium]);

  // Use ref for isOnReviewMeals (avoids stale closure in popstate handler)
  const isOnReviewMealsRef = useRef(isOnReviewMeals);
  useEffect(() => { isOnReviewMealsRef.current = isOnReviewMeals; }, [isOnReviewMeals]);

  // Use ref to always have the latest triesLeft value (avoids stale closure in callbacks)
  const triesLeftRef = useRef(triesLeft);
  useEffect(() => { triesLeftRef.current = triesLeft; }, [triesLeft]);

  // Listen for tryConsumed events to keep triesLeft in sync
  useEffect(() => {
    const handleTryConsumed = () => {
      setTriesLeft(prev => Math.max(0, prev - 1));
    };
    window.addEventListener('tryConsumed', handleTryConsumed);
    return () => window.removeEventListener('tryConsumed', handleTryConsumed);
  }, []);

  // Capacitor: Initialize native plugins (status bar, splash screen)
  useEffect(() => {
    if (isNativeApp()) {
      initStatusBar();
      hideSplashScreen();
    }
  }, []);

  // Capacitor: Listen for deep link callbacks (OAuth, Stripe return)
  useEffect(() => {
    if (!isNativeApp()) return;

    const cleanup = addDeepLinkListener((url) => {
      console.log('[Capacitor] Deep link received:', url);
      try {
        const parsedUrl = new URL(url);
        const token = parsedUrl.searchParams.get('token');
        const userId = parsedUrl.searchParams.get('userId');

        if (token && userId) {
          // OAuth callback — dismiss the SFSafariViewController/Chrome Custom Tab first,
          // then store token and trigger auth flow.
          closeExternalBrowser();

          localStorage.setItem('authToken', token);
          console.log('✅ [Capacitor] OAuth token received via deep link');

          // Dispatch authTokenChanged so the existing listener in App.js picks it up
          window.dispatchEvent(new Event('authTokenChanged'));

          // Navigate to return path
          const oauthReturnPath = localStorage.getItem('oauthReturnPath');
          localStorage.removeItem('oauthReturnPath');
          if (oauthReturnPath && oauthReturnPath !== '/') {
            navigate(oauthReturnPath, { replace: true });
          } else {
            navigate('/wizard', { replace: true });
          }
        } else if (parsedUrl.pathname.includes('/premium/success')) {
          // Stripe checkout success callback
          navigate('/premium/success', { replace: true });
        } else if (parsedUrl.pathname.includes('/premium')) {
          // Stripe checkout cancel callback
          navigate('/premium', { replace: true });
        }
      } catch (e) {
        console.error('[Capacitor] Failed to parse deep link URL:', e);
      }
    });

    return cleanup;
  }, [navigate]);

  // Navigation blocker for browser back button
  // Block when on protected routes AND content is showing (isFadingOut or isInitialized)
  const handleNavigationBlock = useCallback(() => {
    // Premium users: show confirmation only on /reviewmeals (unsaved plan)
    // On /mymeals (already saved), go straight to wizard
    if (isPremiumRef.current) {
      if (isOnReviewMealsRef.current) {
        setShowLeaveMealPlanDrawer(true);
      } else {
        // /mymeals — plan is already saved, just navigate without resetting
        navigate('/wizard', { replace: true, state: { fromStartOver: true } });
      }
      return;
    }
    // No tries left — go straight to premium page, skip the drawer
    if (triesLeftRef.current === 0) {
      navigate('/premium');
      return;
    }
    setShowTriesLeftDrawer(true);
  }, [navigate]);

  // Capacitor: Handle Android hardware back button
  useEffect(() => {
    if (!isNativeApp()) return;

    const cleanup = addBackButtonListener(({ canGoBack }) => {
      // If on a protected route with the navigation blocker active, trigger the blocker
      if (shouldBlockNavigation && (isFadingOut || isInitialized)) {
        handleNavigationBlock();
        return;
      }
      // If on root-level pages, exit the app
      const rootPages = ['/', '/wizard'];
      if (rootPages.includes(location.pathname)) {
        exitApp();
        return;
      }
      // Otherwise, go back in history
      if (canGoBack) {
        window.history.back();
      } else {
        exitApp();
      }
    });

    return cleanup;
  }, [shouldBlockNavigation, isFadingOut, isInitialized, handleNavigationBlock, location.pathname]);

  const { confirmNavigation, cancelNavigation } = useNavigationBlocker(
    shouldBlockNavigation && (isFadingOut || isInitialized),
    handleNavigationBlock
  );

  // Callback for after meal plan is saved - updates state and navigates
  const handleAfterSaveMealPlan = useCallback(() => {
    setHasSavedMealPlan(true);
    navigate('/mymeals', { replace: true });
  }, [navigate]);

  // Handle the special transition from landing to wizard
  const handleStartWizard = () => {
    setWizardCompleted(false);
    setIsInitialLoad(false);
    clearInProgressMealPlan(); // Clear any stale in-progress data
    navigate('/wizard');
  };

  // Handle starting a new meal plan from MyMealPlans
  const handleStartNewMealPlan = async () => {
    // Delete the saved meal plan from backend first
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        await backendClient.delete('/api/mealplan');
      }
    } catch (error) {
      console.error('Failed to delete meal plan:', error);
    }
    
    if (resetMealPlan) {
      resetMealPlan();
    }
    clearInProgressMealPlan(); // Clear in-progress data when starting over
    setHasSavedMealPlan(false);
    setWizardCompleted(false);
    setIsInitialLoad(false);
    navigate('/wizard', { state: { fromStartOver: true } });
  };

  // Safari viewport height fix
  useEffect(() => {
    setSafeViewportHeight();

    const handleResize = () => {
      // Opening a bottom-sheet drawer flips body to position:fixed, which on iOS
      // fires a spurious resize (URL-bar / layout-viewport reflow). Recomputing
      // --vh then resizes the background page and makes it visibly jump behind
      // the drawer. Skip while a scroll lock is active; --vh is already correct
      // from before the drawer opened and self-heals on the close-triggered resize.
      if (isBodyScrollLocked()) return;
      setSafeViewportHeight();
    };
    const handleOrientationChange = () => {
      setTimeout(setSafeViewportHeight, 100);
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  // Mobile browsers can restore a tab from the page cache or after memory pressure
  // without repainting React reliably. Nudge a repaint on resume and auto-reload
  // once if the app shell is genuinely missing, which replaces the manual reload
  // users currently need after a white screen.
  useEffect(() => {
    let blankCheckTimer = null;

    const nudgePaint = () => {
      document.documentElement.classList.add('app-resume-repaint');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.documentElement.classList.remove('app-resume-repaint');
        });
      });
    };

    const hasVisibleAppShell = () => {
      const shell = document.querySelector('[data-app-shell-content="true"]');
      const preloader = document.querySelector('[data-app-preloader="true"]');
      const shellRect = shell?.getBoundingClientRect();
      const preloaderRect = preloader?.getBoundingClientRect();
      const shellVisible = !!shellRect && shellRect.width > 0 && shellRect.height > 0;
      const preloaderVisible = !!preloaderRect && preloaderRect.width > 0 && preloaderRect.height > 0 && !preloader?.classList.contains('hidden');
      return shellVisible || preloaderVisible;
    };

    const recoverOnResume = () => {
      setSafeViewportHeight();
      nudgePaint();
      setResumeTick((tick) => tick + 1);

      if (blankCheckTimer) clearTimeout(blankCheckTimer);
      blankCheckTimer = window.setTimeout(() => {
        if (!hasVisibleAppShell()) {
          reloadAppOnce('blank app shell after resume');
        }
      }, 500);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') recoverOnResume();
    };

    const handlePageShow = () => recoverOnResume();

    const handleRuntimeRecovery = (event) => {
      const message = getErrorMessage(event);
      if (isRecoverableAssetError(message)) {
        reloadAppOnce('stale application assets');
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('error', handleRuntimeRecovery);
    window.addEventListener('unhandledrejection', handleRuntimeRecovery);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (blankCheckTimer) clearTimeout(blankCheckTimer);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('error', handleRuntimeRecovery);
      window.removeEventListener('unhandledrejection', handleRuntimeRecovery);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Register service worker for PWA + push notifications (web only — native uses native push)
  useEffect(() => {
    if (!isNativeApp()) registerServiceWorker();
  }, []);

  // Initialize Analytics (only if cookie consent given)
  useEffect(() => {
    if (isAnalyticsAllowed()) {
      initConsentedAnalytics(initAmplitude);
    }
    
    // Re-initialize when consent changes
    const handleConsentChange = (e) => {
      if (e.detail?.analytics) {
        initConsentedAnalytics(initAmplitude);
      }
    };
    window.addEventListener('cookieConsentChanged', handleConsentChange);
    return () => window.removeEventListener('cookieConsentChanged', handleConsentChange);
  }, []);

  // Initialize the country on app startup
  useEffect(() => {
    const initialize = async () => {
      try {
        await initializeUserCountry();
        const country = await getUserCountry();
        setUserCountry(country);
        
        // Set initial user properties for analytics
        const token = localStorage.getItem('authToken');
        const language = navigator.language || 'en';
        setUserProperties({
          country,
          language: language.split('-')[0], // 'en-US' -> 'en'
          is_guest: !token,
          is_registered: !!token,
        });
      } catch (error) {
        console.error('Error initializing country:', error);
      }
    };
    
    initialize();
  }, []);

  // Listen for auth token changes (login) and redirect premium users away from /premium page
  useEffect(() => {
    const handleAuthChange = async () => {
      const token = localStorage.getItem('authToken');
      const currentPath = window.location.pathname;
      
      if (token && currentPath === '/premium') {
        // User just logged in while on /premium page - check if they're premium
        try {
          const fingerprint = getFingerprint();
          const queryParams = fingerprint ? `?fingerprint=${encodeURIComponent(fingerprint)}` : '';
          const limitData = await backendClient.get(`/api/meal-plan-limit/check${queryParams}`);
          
          if (limitData.isPremium) {
            // User is premium - redirect them to appropriate page
            setIsPremium(true);
            
            // Check if they have a saved meal plan
            try {
              const mealPlanData = await backendClient.get('/api/mealplan');
              if (mealPlanData && mealPlanData.mealPlan && mealPlanData.mealPlan.length > 0 && mealPlanData.isSaved) {
                // Has saved meal plan - go to mymeals
                setHasSavedMealPlan(true);
                navigate('/mymeals', { replace: true });
              } else {
                // No saved meal plan - go to wizard
                navigate('/wizard', { replace: true });
              }
            } catch (mealPlanError) {
              // No meal plan - go to wizard
              navigate('/wizard', { replace: true });
            }
          }
        } catch (error) {
          console.warn('Failed to check premium status after login:', error.message);
        }
      }
    };

    window.addEventListener('authTokenChanged', handleAuthChange);
    
    return () => {
      window.removeEventListener('authTokenChanged', handleAuthChange);
    };
  }, [navigate]);

  // Handle country change
  const handleCountryChange = async (newCountry) => {
    try {
      await setUserCountry(newCountry);
      setUserCountry(newCountry);
      
      if (wizardCompleted) {
        console.log(`Country changed to ${newCountry}, consider refreshing meal plan`);
      }
    } catch (error) {
      console.error('Error changing country:', error);
    }
  };

  // Check for existing meal plan on app startup ONLY (initial mount)
  useEffect(() => {
    // Skip if already initialized (prevents re-running on route changes)
    if (isInitialized) return;
    
    const checkExistingMealPlan = async () => {
      const currentPath = window.location.pathname;
      
      // Handle OAuth callback - check for token in URL params
      const urlParams = new URLSearchParams(window.location.search);
      const oauthToken = urlParams.get('token');
      const oauthUserId = urlParams.get('userId');
      
      let justLoggedInViaOAuth = false;
      
      if (oauthToken && oauthUserId) {
        // iOS native: SFSafariViewController loaded this web page with native=ios.
        // JS-redirect to the custom URL scheme — SFSafariViewController allows this,
        // unlike server-side 302 redirects to custom schemes which it blocks.
        const nativePlatform = urlParams.get('native');
        if (nativePlatform === 'ios') {
          window.location.href = `ai.9meals.api://?token=${oauthToken}&userId=${oauthUserId}`;
          return;
        }

        // Store the token from OAuth callback
        localStorage.setItem('authToken', oauthToken);
        console.log('✅ OAuth: Token stored, user logged in');
        justLoggedInViaOAuth = true;
        
        // Transfer anonymous tries to authenticated account
        try {
          const fingerprint = getFingerprint();
          const transferData = { fingerprint };
          console.log('🔄 Transferring anonymous tries to account after OAuth login:', transferData);
          await backendClient.post('/api/meal-plan-limit/transfer-to-account', transferData);
          console.log('✅ Tries transferred successfully');
        } catch (transferError) {
          // Transfer failure is not critical - log and continue
          console.log('ℹ️ Tries transfer failed (may be expected for new users):', transferError.message);
        }
        
        // Clean up URL - remove token params
        window.history.replaceState({}, document.title, currentPath);
        
        // Push local language to backend (new user gets default 'es', but user may have chosen 'ru')
        try {
          const localLanguage = getCurrentLanguage();
          if (localLanguage && localLanguage !== 'es') {
            await backendClient.put('/api/user/language', { language: localLanguage });
            console.log('✅ Language pushed to backend after OAuth:', localLanguage);
          }
        } catch (langError) {
          console.warn('ℹ️ Language push failed (non-critical):', langError.message);
        }

        // Push local preferences to backend so fetchPreferences won't overwrite with defaults
        try {
          const storedPrefs = localStorage.getItem('9meals_preferences');
          if (storedPrefs) {
            const localPrefs = JSON.parse(storedPrefs);
            await backendClient.put('/api/user/preferences', localPrefs);
            console.log('✅ Preferences pushed to backend after OAuth:', localPrefs);
          }
        } catch (prefsError) {
          console.warn('ℹ️ Preferences push failed (non-critical):', prefsError.message);
        }

        // Check if there's an in-progress meal plan that needs to be saved
        const inProgressData = getInProgressMealPlan();
        if (inProgressData && inProgressData.mealPlan && inProgressData.mealPlan.length > 0) {
          console.log('🔄 Found in-progress meal plan after OAuth, auto-saving...');
          try {
            const mealPlanData = {
              mealPlan: inProgressData.mealPlan,
              cookingStyle: (() => {
                try {
                  const storedPrefs = localStorage.getItem('9meals_preferences');
                  if (storedPrefs) {
                    const prefs = JSON.parse(storedPrefs);
                    if (prefs.cookingStyle) return prefs.cookingStyle;
                  }
                } catch (e) { /* fall through to default */ }
                return {
                  breakfast: { cookingTime: 'standard', cookFor2Days: false },
                  lunch: { cookingTime: 'standard', cookFor2Days: false },
                  dinner: { cookingTime: 'standard', cookFor2Days: false }
                };
              })(),
              groceryList: {
                products: inProgressData.productList || [],
                totalCost: inProgressData.totalCost,
                adjustedTotalCost: inProgressData.adjustedTotalCost,
                haveItState: inProgressData.haveItState || {},
                leftovers: inProgressData.leftovers || [],
              },
            };
            
            await backendClient.post('/api/mealplan/save', mealPlanData, { timeout: 30000 });
            console.log('✅ In-progress meal plan auto-saved after OAuth login');
            
            // Clear in-progress data since it's now saved
            clearInProgressMealPlan();
          } catch (saveError) {
            console.error('❌ Failed to auto-save in-progress meal plan:', saveError);
            // Continue anyway - user can save manually later
          }
        }
      }
      
      // After OAuth, navigate back to the page the user was on (e.g. /premium)
      if (justLoggedInViaOAuth) {
        const oauthReturnPath = localStorage.getItem('oauthReturnPath');
        localStorage.removeItem('oauthReturnPath');
        if (oauthReturnPath && oauthReturnPath !== '/') {
          navigate(oauthReturnPath, { replace: true });
          setIsFadingOut(true);
          setTimeout(() => {
            setIsInitialized(true);
            setIsInitialLoad(false);
          }, 500);
          return;
        }
      }

      const token = localStorage.getItem('authToken');
      
      // For ANY route on initial load, redirect to landing first if we need to check state
      // The landing page will handle the redirect after checking
      if (currentPath !== '/') {
        // Check if we have in-progress data for /reviewmeals
        if (currentPath === '/reviewmeals' && hasInProgressMealPlan()) {
          // Has in-progress data, allow staying on reviewmeals
          setWizardCompleted(true);
          setIsFadingOut(true);
          setTimeout(() => {
            setIsInitialized(true);
            setIsInitialLoad(false);
          }, 500);
          return;
        }
        
        // For other routes, redirect to landing which will do the proper check
        // Don't redirect yet - continue with the check below
      }
      
      if (!token) {
        // Fetch tries for guest users using fingerprint
        try {
          const fingerprint = getFingerprint();
          if (fingerprint) {
            const limitData = await backendClient.get(`/api/meal-plan-limit/check?fingerprint=${encodeURIComponent(fingerprint)}`);
            if (limitData.triesRemaining !== undefined && limitData.triesRemaining !== 'unlimited') {
              setTriesLeft(limitData.triesRemaining);
            }
          }
        } catch (e) {
          // Non-critical — uses default triesLeft
        }

        // If on protected route without auth, redirect appropriately
        if (currentPath === '/mymeals') {
          navigate('/', { replace: true });
        } else if (currentPath === '/reviewmeals') {
          // No token and no in-progress data - go to wizard
          navigate('/wizard', { replace: true });
        }
        setIsFadingOut(true);
        setTimeout(() => {
          setIsInitialized(true);
          setIsInitialLoad(false);
        }, 500);
        return;
      }

      try {
        const fingerprint = getFingerprint();
        const queryParams = fingerprint ? `?fingerprint=${encodeURIComponent(fingerprint)}` : '';
        
        let userIsPremium = false; // Local variable to use in this function
        try {
          const limitData = await backendClient.get(`/api/meal-plan-limit/check${queryParams}`);
          if (limitData.triesRemaining !== undefined && limitData.triesRemaining !== 'unlimited') {
            setTriesLeft(limitData.triesRemaining);
          }
          if (limitData.isPremium !== undefined) {
            setIsPremium(limitData.isPremium);
            userIsPremium = limitData.isPremium;
          }
        } catch (limitError) {
          console.warn('Failed to check meal plan limit:', limitError.message);
        }

        const data = await backendClient.get('/api/mealplan');
        
        if (data) {
          if (data.mealPlan && data.mealPlan.length > 0 && data.isSaved) {
            // Debug: Log meal plan data from database
            console.log('📦 Loaded meal plan from DB - checking cookingTime:');
            data.mealPlan.forEach((recipe, idx) => {
              console.log(`  [${idx}] ${recipe.title}: cookingTime = "${recipe.cookingTime}"`);
            });
            

            if (data.productList && Array.isArray(data.productList)) {
              data.productList.forEach((category, cIdx) => {
                if (category.items && Array.isArray(category.items)) {
                  category.items.slice(0, 3).forEach((item, iIdx) => {
                    console.log(`  [${cIdx}][${iIdx}] ${item.productName}: selectedSize.image = "${item.selectedSize?.image || 'MISSING'}"`);
                  });
                }
              });
            }

            if (data.leftovers && Array.isArray(data.leftovers)) {
              data.leftovers.slice(0, 3).forEach((item, idx) => {
                console.log(`  Leftover [${idx}] ${item.productName}: selectedSize.image = "${item.selectedSize?.image || 'MISSING'}"`);
              });
            }
            
            // Load the meal plan data into context
            if (setMealPlan) setMealPlan(data.mealPlan);
            if (data.productList && setProductList) setProductList(data.productList);
            if (data.totalCost && setTotalCost) setTotalCost(data.totalCost);
            if (data.maxStorageTime && setMaxStorageTime) setMaxStorageTime(data.maxStorageTime);
            if (data.leftovers && setLeftovers) setLeftovers(data.leftovers);
            if (data.shoppingModeState && setShoppingModeState) {
              setShoppingModeState(data.shoppingModeState);
            }
            if (data.haveItState && setHaveItState) {
              setHaveItState(data.haveItState);
            }
            if (data.myProducts && setMyProducts) {
              setMyProducts(data.myProducts);
            }
            if (data.cookingStyle) {
              setSavedCookingStyle(data.cookingStyle);
            } else if (data.cookingFrequency) {
              // Migrate old format to new format
              const migratedStyle = {
                breakfast: { cookingTime: 'standard', cookFor2Days: data.cookingFrequency.breakfast === 2 },
                lunch: { cookingTime: 'standard', cookFor2Days: data.cookingFrequency.lunch === 2 },
                dinner: { cookingTime: 'standard', cookFor2Days: data.cookingFrequency.dinner === 2 }
              };
              setSavedCookingStyle(migratedStyle);
            }
            
            setWizardCompleted(true);
            setHasSavedMealPlan(true);
            setIsAuthenticated(true);
            
            // Always redirect to /mymeals when user has a saved plan
            // This handles: /, /wizard, /reviewmeals - all go to mymeals
            // Exception: /testing and other static pages should stay (but NOT /premium for premium users)
            // Exception: /admin routes should never be affected by user meal plan state
            const staticPages = ['/aboutus', '/privacy', '/cookies', '/contact', '/myprofile', '/favorites', '/testing', '/premium'];
            const isAdminRoute = currentPath.startsWith('/admin');
            const isSharedGroceryRoute = currentPath.startsWith('/shared-grocery/');
            const isMyMealsRoute = currentPath === '/mymeals' || currentPath.startsWith('/mymeals/');
            // Premium users with saved meal plan should be redirected from /premium to /mymeals
            const shouldRedirectFromPremium = currentPath === '/premium' && userIsPremium;
            if (((!staticPages.includes(currentPath) && !isAdminRoute && !isSharedGroceryRoute && !isMyMealsRoute) || shouldRedirectFromPremium)) {
              navigate('/mymeals', { replace: true });
            }
          } else {
            // Token exists but no saved meal plan - redirect based on route
            if (currentPath === '/mymeals') {
              // No saved plan but trying to access mymeals - go to landing
              navigate('/', { replace: true });
            } else if (currentPath === '/reviewmeals') {
              // No saved plan and no in-progress data on reviewmeals - go to wizard
              navigate('/wizard', { replace: true });
            } else if (currentPath === '/premium' && userIsPremium) {
              // Premium user on /premium page without saved meal plan - go to wizard
              navigate('/wizard', { replace: true });
            }
            // For /wizard and /, stay on current route
          }
        } else {
          // API error response
          if (currentPath === '/mymeals') {
            navigate('/', { replace: true });
          } else if (currentPath === '/reviewmeals') {
            navigate('/wizard', { replace: true });
          }
        }
      } catch (error) {
        console.error('Error checking meal plan:', error);
        
        // Handle auth errors (token expired/invalid)
        if (error.isAuthError) {
          setIsAuthenticated(false);
          if (currentPath === '/mymeals' || currentPath === '/reviewmeals') {
            navigate('/', { replace: true });
          }
        } else if (currentPath === '/mymeals') {
          navigate('/', { replace: true });
        } else if (currentPath === '/reviewmeals') {
          navigate('/wizard', { replace: true });
        }
      } finally {
        setIsFadingOut(true);
        setTimeout(() => {
          setIsInitialized(true);
          setIsInitialLoad(false);
        }, 500);
      }
    };

    checkExistingMealPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler for logo click
  const resolvePremiumStatus = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return false;

    try {
      const fingerprint = getFingerprint();
      const queryParams = fingerprint ? `?fingerprint=${encodeURIComponent(fingerprint)}` : '';
      const limitData = await backendClient.get(`/api/meal-plan-limit/check${queryParams}`);

      if (limitData?.isPremium !== undefined) {
        setIsPremium(limitData.isPremium);
        return !!limitData.isPremium;
      }
    } catch (error) {
      console.warn('Failed to refresh premium status on logo click:', error.message);
    }

    return isPremiumRef.current;
  };

  const handleLogoClick = async () => {
    // On /mymeals, clicking the logo is a no-op (user is already at their
    // home — the logo shouldn't do anything).
    if (isOnMyMeals) {
      return;
    }

    let currentPremium = isPremiumRef.current;
    if (!currentPremium && localStorage.getItem('authToken')) {
      currentPremium = await resolvePremiumStatus();
    }

    // If on /reviewmeals, show TriesLeftDrawer (unless premium)
    if (isOnReviewMeals) {
      if (currentPremium) {
        // Show confirmation on /reviewmeals (unsaved plan)
        setShowLeaveMealPlanDrawer(true);
        return;
      }
      // Free user — always show the drawer; it self-fetches triesRemaining
      // and renders either the "tries available" or "Become Premium" state.
      setShowTriesLeftDrawer(true);
      return;
    }
    
    if (isAuthenticated && hasSavedMealPlan) {
      navigate('/mymeals');
    } else if (currentPremium) {
      // Premium users without saved plan go to wizard, not landing
      navigate('/wizard');
    } else {
      navigate('/');
    }
  };

  // Handler for showing MyMealPlans
  const handleShowMyMeals = () => {
    if (isAuthenticated && hasSavedMealPlan) {
      navigate('/mymeals');
    }
  };

  // Handler for back button from MealPlan
  const handleBackFromMealPlan = async () => {
    // Premium status may not yet be resolved if checkExistingMealPlan hit the
    // in-progress early-return on /reviewmeals (it skips /api/meal-plan-limit/check).
    // Lazily resolve for logged-in users so premium gets the correct drawer.
    let currentPremium = isPremiumRef.current;
    if (!currentPremium && localStorage.getItem('authToken')) {
      currentPremium = await resolvePremiumStatus();
    }

    // Premium users: show confirmation only on /reviewmeals (unsaved plan)
    if (currentPremium) {
      if (isOnReviewMeals) {
        setShowLeaveMealPlanDrawer(true);
      } else {
        // /mymeals — plan is already saved, just navigate without resetting
        navigate('/wizard', { replace: true, state: { fromStartOver: true } });
      }
      return;
    }
    // Free user — always show the drawer; it self-fetches triesRemaining
    // and renders either the "tries available" or "Become Premium" state.
    setShowTriesLeftDrawer(true);
  };

  // Handle closing the tries left drawer
  const handleCloseTriesLeftDrawer = () => {
    setShowTriesLeftDrawer(false);
    cancelNavigation();
  };

  // Handle leave meal plan confirmation (premium users)
  const handleConfirmLeaveMealPlan = () => {
    setShowLeaveMealPlanDrawer(false);
    if (resetMealPlan) resetMealPlan();
    clearInProgressMealPlan();
    setWizardCompleted(false);
    setIsInitialLoad(false);
    navigate('/wizard', { replace: true, state: { fromStartOver: true } });
  };

  const handleCloseLeaveMealPlanDrawer = () => {
    setShowLeaveMealPlanDrawer(false);
    cancelNavigation();
  };

  // Handle "Try Again" button from the tries left drawer
  const handleTryAgainFromDrawer = async () => {
    setShowTriesLeftDrawer(false);
    
    // If coming from /mymeals, we need to delete the saved meal plan from backend
    if (isOnMyMeals && hasSavedMealPlan) {
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          await backendClient.delete('/api/mealplan');
        }
      } catch (error) {
        console.error('Failed to delete meal plan:', error);
      }
    }
    
    // Reset all relevant state
    if (resetMealPlan) {
      resetMealPlan();
    }
    clearInProgressMealPlan(); // Clear in-progress data when trying again
    setHasSavedMealPlan(false);
    setWizardCompleted(false);
    setIsInitialLoad(false);
    confirmNavigation('/wizard');
  };

  // Handler for back button from UserWizard
  const handleBackFromUserWizard = () => {
    setWizardCompleted(false);
    navigate('/');
  };

  // Handler for wizard completion - navigate to review meals
  const handleWizardComplete = () => {
    // Clear any existing in-progress meal plan when starting a new one
    clearInProgressMealPlan();
    setWizardCompleted(true);
    setIsInitialLoad(false);
    // Pass state to indicate navigation came from wizard completion
    navigate('/reviewmeals', { state: { fromWizard: true } });
  };

  // Preloader overlay that fades out smoothly
  const preloader = (
    <div 
      data-app-preloader="true"
      className={`fixed inset-0 z-[100] text-center py-8 px-8 min-h-safe-screen flex flex-col items-center justify-center bg-[#FFCB00] transition-opacity duration-500 ${isFadingOut ? 'opacity-0 pointer-events-none' : 'opacity-100'} ${isInitialized ? 'hidden' : ''}`}
    >
      <div className="mx-auto items-center justify-center" style={{ maxWidth: '300px' }}>
        <DotLottieReact 
          className='mx-auto'
          src="https://lottie.host/ab66b03d-6127-4dfc-a389-8021d354c8df/357358nYdS.lottie"
          loop
          autoplay
          width={300}
          height={300}
        />
      </div>
      <p className="
        text-[var(--Color-Grays-Gray-10,#0D0C07)]
        text-[16.8px]
        font-semibold
        leading-[32px]
        mb-3"></p>
    </div>
  );

  // Determine if we're on the landing page
  const isLandingPage = location.pathname === '/';
  const isWizardPage = location.pathname === '/wizard';
  const isAdminPage = location.pathname.startsWith('/admin');
  const isPremiumPage = location.pathname === '/premium';
  const isSharedGroceriesPage = location.pathname.startsWith('/shared-grocery/');
  const isStaticPage = ['/aboutus', '/privacy', '/cookies', '/contact', '/myprofile', '/favorites', '/testing'].includes(location.pathname);
  const isCookingModePage = /^\/mymeals\/recipe\/\d+\/cook$/.test(location.pathname);
  const isRecipeDetailPage = /^\/mymeals\/recipe\/\d+$/.test(location.pathname);
  
  // Check if user came from "Start Over" flow - hide back button in these cases
  const isFromStartOver = location.state?.fromStartOver === true || location.state?.fromMealPlanReview === true;

  // Handle back click based on current page
  const handleBackClick = () => {
    if (location.pathname === '/mymeals/groceries') {
      navigate('/mymeals');
    } else if (isRecipeDetailPage) {
      navigate('/mymeals');
    } else if (isSharedGroceriesPage) {
      navigate('/');
    } else if (isPremiumPage) {
      // Go back to where the user came from (e.g. /reviewmeals, /mymeals)
      navigate(-1);
    } else if (isStaticPage) {
      navigate(-1);
    } else if (isOnReviewMeals) {
      handleBackFromMealPlan();
    } else {
      handleBackFromUserWizard();
    }
  };

  // Show preloader and render content behind it (hidden) when fading out starts
  // This allows the content to be ready when preloader fades out
  const showContent = isFadingOut || isInitialized;

  return (
    <>
      {!isInitialized && preloader}
      {showContent && (
        <>
          {!isAdminPage && !isCookingModePage && (
            <Header 
              userCountry={userCountry}
              onCountryChange={handleCountryChange}
              onLogoClick={handleLogoClick}
              onShowMyMeals={handleShowMyMeals}
              isLandingPage={isLandingPage}
              showCountrySelector={isLandingPage || isWizardPage}
              showLanguageSelector={isLandingPage || isWizardPage}
              showBackButton={!isLandingPage && !isOnMyMeals && !isFromStartOver}
              onBackClick={handleBackClick}
              animate={isFadingOut}
            />
          )}
          
          <div className="hero-to-wizard-transition hero-bg-responsive min-h-safe-screen" data-app-shell-content="true" data-resume-tick={resumeTick}>
            <ScrollToTop />
            <Routes>
              {/* Landing Page Route */}
              <Route
                path="/"
                element={
                  <LandingGuard hasSavedMealPlan={hasSavedMealPlan} isInitialLoad={isInitialLoad} isPremium={isPremium}>
                    <LandingPage onStart={handleStartWizard} />
                  </LandingGuard>
                }
              />

              {/* Wizard Route */}
              <Route 
                path="/wizard" 
                element={
                  <WizardGuard hasSavedMealPlan={hasSavedMealPlan} isInitialLoad={isInitialLoad}>
                    <main>
                      <UserWizard 
                        onFinish={handleWizardComplete} 
                        onBack={handleBackFromUserWizard}
                      />
                    </main>
                  </WizardGuard>
                } 
              />
          
              {/* Review Meals Route */}
              <Route 
                path="/reviewmeals" 
                element={
                  <ReviewMealsGuard hasSavedMealPlan={hasSavedMealPlan} wizardCompleted={wizardCompleted}>
                    <main>
                      <MealPlanComponent 
                        onNavigateToWizard={() => {
                          setWizardCompleted(false);
                          navigate('/wizard', { state: { fromMealPlanReview: true } });
                        }}
                        onSaveMealPlan={handleAfterSaveMealPlan}
                      />
                    </main>
                  </ReviewMealsGuard>
                } 
              />
          
              {/* My Meals Routes — meals list and groceries page */}
              <Route
                path="/mymeals"
                element={
                  <MyMealsGuard hasSavedMealPlan={hasSavedMealPlan}>
                    <main>
                      <MyMealPlans
                        mealPlan={mealPlan}
                        productList={productList}
                        totalCost={totalCost}
                        maxStorageTime={maxStorageTime}
                        leftovers={leftovers}
                        imagesLoading={{}}
                        initialShoppingModeState={shoppingModeState}
                        initialHaveItState={haveItState}
                        onStartNewMealPlan={handleStartNewMealPlan}
                        savedCookingStyle={savedCookingStyle}
                        triesLeft={triesLeft}
                        onTriesUpdate={setTriesLeft}
                      />
                    </main>
                  </MyMealsGuard>
                }
              />
              <Route
                path="/mymeals/groceries"
                element={
                  <MyMealsGuard hasSavedMealPlan={hasSavedMealPlan}>
                    <main>
                      <GroceriesPage
                        productList={productList}
                        maxStorageTime={maxStorageTime}
                        leftovers={leftovers}
                        initialShoppingModeState={shoppingModeState}
                        initialHaveItState={haveItState}
                        onCreateShareLink={async () => {
                          const result = await import('./services/apiService').then((service) =>
                            service.createSharedGroceryLink()
                          );
                          const url = new URL(
                            `/shared-grocery/${result.shareToken}`,
                            window.location.origin
                          ).toString();
                          return { ...result, url };
                        }}
                      />
                    </main>
                  </MyMealsGuard>
                }
              />
              <Route
                path="/mymeals/recipe/:recipeIndex"
                element={
                  <MyMealsGuard hasSavedMealPlan={hasSavedMealPlan}>
                    <main>
                      <RecipeDetailPage />
                    </main>
                  </MyMealsGuard>
                }
              />
              <Route
                path="/mymeals/recipe/:recipeIndex/cook"
                element={
                  <MyMealsGuard hasSavedMealPlan={hasSavedMealPlan}>
                    <CookingModePage />
                  </MyMealsGuard>
                }
              />
              <Route
                path="/shared-grocery/:shareToken"
                element={
                  <main>
                    <SharedGroceriesPage />
                  </main>
                }
              />

              {/* About Us Route */}
              <Route 
                path="/aboutus" 
                element={<AboutUs />} 
              />

              {/* Privacy Policy Route */}
              <Route 
                path="/privacy" 
                element={<PrivacyPolicy />} 
              />

              {/* Cookie Policy Route */}
              <Route 
                path="/cookies" 
                element={<CookiePolicy />} 
              />

              {/* Contact Us Route */}
              <Route 
                path="/contact" 
                element={<ContactUs />} 
              />

              {/* Legal Notice / Aviso Legal Route */}
              <Route 
                path="/legal" 
                element={<AvisoLegal />} 
              />

              {/* My Profile Route */}
              <Route 
                path="/myprofile" 
                element={<MyProfile />} 
              />

              {/* Favorite Meals Route */}
              <Route 
                path="/favorites" 
                element={<FavoriteMeals />} 
              />

              {/* Promo Landing Page (public - no auth required) */}
              <Route
                path="/promo/:id"
                element={<PromoLandingPage />}
              />

              {/* Premium Route */}
              <Route 
                path="/premium" 
                element={<Premium />} 
              />

              {/* Premium Success Route */}
              <Route 
                path="/premium/success" 
                element={<PremiumSuccess />} 
              />

              {/* Testing Route */}
              <Route 
                path="/testing" 
                element={<Testing />} 
              />

              {/* Admin Panel */}
              <Route 
                path="/admin/*" 
                element={<AdminApp />} 
              />
          
              {/* 404 - Page Not Found */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
          
          {/* Tries Left Drawer */}
          <TriesLeftDrawer
            isOpen={showTriesLeftDrawer}
            onClose={handleCloseTriesLeftDrawer}
            onTryAgain={handleTryAgainFromDrawer}
            triesLeft={triesLeft}
            onTriesUpdate={setTriesLeft}
            isRegistered={!!localStorage.getItem('authToken') || isAuthenticated}
          />

          {/* Leave Meal Plan Confirmation (premium users) */}
          <ReplaceMealPlanDrawer
            isOpen={showLeaveMealPlanDrawer}
            onClose={handleCloseLeaveMealPlanDrawer}
            onConfirmReplace={handleConfirmLeaveMealPlan}
            i18nPrefix="leaveMealPlan"
            isPremium={isPremium}
            triesLeft={triesLeft}
          />

          {/* Cookie Consent Banner */}
          {!isNativeApp() && <CookieConsent />}

          {/* PWA Install Prompt */}
          <InstallPrompt />

          {/* Push Notification Prompt — shown after first meal plan generation */}
          <NotificationPrompt triggerReady={hasSavedMealPlan} />
        </>
      )}
    </>
  );
}

// Main App component
function App() {
  return (
    <UserProvider>
      <MealPlanProvider>
        <AppContent />
      </MealPlanProvider>
    </UserProvider>
  );
}

export default App;